import InputAdornment from "@mui/material/InputAdornment";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { preventDefaultBehaviour } from "designSystem/utilities/prevent-default-behavior";
import React, { FunctionComponent, KeyboardEventHandler, MouseEventHandler, useRef } from "react";
import IconButton from "../button/IconButton";
import TextField, { TextFieldProps } from "./TextField";

export interface SearchTextFieldProps extends Omit<TextFieldProps, "onKeyDown"> {
	/** indicate if TAB key should be treated like ESC key */
	handleTabAsEscape?: boolean;

	/** hide clear button from being used to clear input */
	hideClearButton?: boolean;
}

const SearchTextField: FunctionComponent<SearchTextFieldProps> = (props) => {
	const {
		handleTabAsEscape,
		hideClearButton,
		InputProps,
		onEscape,
		onTab,
		...otherProps
	} = props;

	const textInput = useRef(null);
	const showEndAdornment = !hideClearButton && props.autoFocus && textInput.current?.value?.toString().length > 0;

	const _onKeyDown: KeyboardEventHandler<HTMLDivElement> = (event) => {
		const { key } = event;

		// up/down keys will move cursor to start/end of input, but we only want
		// them to allow selection of search results
		if (key === "ArrowDown" || key === "ArrowUp" || key === "Escape" || (handleTabAsEscape && (key === "Tab"))) {
			event.preventDefault();
			event.stopPropagation();
		}
	};

	const onClose: MouseEventHandler<HTMLButtonElement> = (event) => {
		// Programmatically changing value of text field is not throwing onChange event automatically. Issue : https://github.com/facebook/react/issues/11095
		event.preventDefault();
		const inputRef = textInput.current;
		inputRef.focus();
		const previousValue = inputRef.value;
		inputRef.value = "";
		const tracker = inputRef._valueTracker;
		if (tracker) {
			tracker.setValue(previousValue);
		}
		inputRef.dispatchEvent(new Event("change", { bubbles: true }));
	};

	return (
		<TextField
			{...otherProps}
			InputProps={{
				...InputProps,
				startAdornment: (
					<InputAdornment position="start">
						<SearchIcon color="action" />
					</InputAdornment>
				),
				endAdornment: showEndAdornment && (
					<InputAdornment position="end">
						<IconButton
							aria-label="escape"
							customSize={16}
							design="secondary"
							onClick={onClose}
							onMouseDown={preventDefaultBehaviour}
							round
							size="large"
						>
							<CloseIcon style={{ fontSize: 14 }} />
						</IconButton>
					</InputAdornment>
				),
			}}
			inputRef={textInput}
			onEscape={onEscape}
			onKeyDown={_onKeyDown}
			onTab={onTab || (handleTabAsEscape ? onEscape : null)}
		/>
	);
};

export default SearchTextField;
